/**
 * Fit (W2-11 batch-tools; DESIGN.md §5.6.5 "Fit (UNDOC-2)", RPE MultiEvent 拟合): many dense keys (typically the
 * Linear pieces of a bake or an imported RPE curve) become fewer eased segments within a tolerance.
 *
 * For each run of consecutive selected segments of a numeric track, greedily from the run's first key: the span is
 * extended key by key (looking up to 48 keys past a misfit) to the farthest key where ONE segment from the first key's leave value to the last key's arrive value, with some
 * RPE easing (ids 1..29, whole curve, allowed for the property), stays within `tolerance` (property units) of the
 * original curve at every inner key and at three points inside every original segment (progress in seconds, as
 * Phira interpolates). Inner keys must be continuous (no jumps). The best-fitting ease is kept and the inner keys
 * are removed. On a line with position X/Y linked, both position axes are fitted together over the same beats, so
 * their keys stay at shared beats (each axis may get its own ease).
 */

import { Ease } from '../ease/index';
import type { Ease as EaseValue } from '../ease/types';
import type { KeyId, TrackId } from '../model/types';
import type { TxBuilder } from '../ops/types';
import { Beats } from '../time/beat';
import type { MessageRef } from '../util/types';
import {
  cleanLinks,
  commitKeys,
  easeUseFor,
  easeY,
  keyAtBeat,
  readKeys,
  requireTrack,
  xyPartnerTrack,
  type KeyRec,
  type TrackInfo,
} from '../commands/kit';
import { toolReason, TOOLS_MSG } from './common';
import type { ToolReport } from './types';

/** Points measured inside every original segment. */
const INNER_SAMPLES = [0.25, 0.5, 0.75] as const;
/** Consecutive misfits after which a run stops growing. */
const FIT_LOOKAHEAD = 48;
/** Candidate easing ids. */
const CANDIDATE_IDS: readonly number[] = Array.from({ length: 29 }, (_, i) => i + 1);

export interface FitParams {
  readonly segments: readonly KeyId[];
  readonly tolerance: number;
}

interface FitAxis {
  readonly info: TrackInfo;
  readonly keys: KeyRec[];
  readonly secs: number[];
}

function num(v: unknown): number {
  return typeof v === 'number' ? v : Number.NaN;
}

/** Original value of an axis at second `sec` inside key range [i, j]. */
function originalAt(axis: FitAxis, k: number, q: number): number {
  const a = axis.keys[k] as KeyRec;
  const b = axis.keys[k + 1] as KeyRec;
  if (a.ease.kind === 'hold') return num(a.vOut);
  return num(a.vOut) + (num(b.vIn) - num(a.vOut)) * easeY(a.ease, q, axis.info.extra);
}

/** Best whole-curve ease for keys i..j of an axis and its max deviation; null when the inner keys jump. */
function bestEase(tx: TxBuilder, axis: FitAxis, i: number, j: number): { ease: EaseValue; error: number } | null {
  const { keys, secs } = axis;
  for (let k = i + 1; k < j; k++) {
    const key = keys[k] as KeyRec;
    if (num(key.vIn) !== num(key.vOut)) return null;
  }
  const start = keys[i] as KeyRec;
  const end = keys[j] as KeyRec;
  const v0 = num(start.vOut);
  const v1 = num(end.vIn);
  const s0 = secs[i] as number;
  const s1 = secs[j] as number;
  // Sample points: (progress over [i, j], original value).
  const pts: [number, number][] = [];
  for (let k = i; k < j; k++) {
    const sa = secs[k] as number;
    const sb = secs[k + 1] as number;
    if (k > i) pts.push([(sa - s0) / (s1 - s0), num((keys[k] as KeyRec).vOut)]);
    for (const q of INNER_SAMPLES) pts.push([(sa + (sb - sa) * q - s0) / (s1 - s0), originalAt(axis, k, q)]);
  }
  let best: { ease: EaseValue; error: number } | null = null;
  const use = easeUseFor(tx, axis.info, start.vOut, end.vIn);
  for (const id of CANDIDATE_IDS) {
    const ease: EaseValue = { kind: 'tween', id, left: 0, right: 1 };
    if (Ease.validateFor(ease, use) !== null) continue;
    let err = 0;
    for (const [p, v] of pts) {
      const y = id === 1 ? p : Ease.modelY(ease, p);
      err = Math.max(err, Math.abs(v0 + (v1 - v0) * y - v));
      if (best !== null && err >= best.error) break;
    }
    if (best === null || err < best.error) best = { ease, error: err };
  }
  return best;
}

/** Groups of tracks fitted together (X/Y-linked partners), with the selected start beats. */
function groupsOf(tx: TxBuilder, ids: readonly KeyId[]): { tracks: TrackId[]; beats: Set<string> }[] {
  const groups = new Map<string, { tracks: TrackId[]; beats: Set<string> }>();
  for (const id of ids) {
    const k = tx.doc.keys.get(id);
    if (k === undefined || tx.idx.nextKey(id) === undefined) continue;
    const info = requireTrack(tx, k.track);
    if (info.valueType !== 'number') continue;
    const partner = xyPartnerTrack(tx, info);
    const tracks = partner === undefined ? [k.track] : [k.track, partner].sort((a, b) => a - b);
    const key = tracks.join(':');
    const g = groups.get(key) ?? { tracks, beats: new Set<string>() };
    g.beats.add(Beats.key(k.t));
    groups.set(key, g);
  }
  return [...groups.values()];
}

export function fit(tx: TxBuilder, p: FitParams): ToolReport {
  if (!Number.isFinite(p.tolerance) || p.tolerance <= 0) tx.fail(toolReason(TOOLS_MSG.fitTolerance));
  const groups = groupsOf(tx, p.segments);
  if (groups.length === 0) tx.fail(toolReason(TOOLS_MSG.fitNothing));
  const unlinked = new Set<number>();
  let before = 0;
  let after = 0;
  let removed = 0;
  let maxError = 0;
  const segments: KeyId[] = [];
  for (const g of groups) {
    const axes: FitAxis[] = g.tracks.map((t) => {
      const keys = readKeys(tx, t);
      return { info: requireTrack(tx, t), keys, secs: keys.map((k) => tx.tempo.secAt(k.t)) };
    });
    const main = axes[0] as FitAxis;
    // Beats shared by every axis (X/Y-linked axes are fitted on common beats only).
    const shared = main.keys.map((k) => k.t).filter((t) => axes.every((a) => a.keys.some((k) => Beats.eq(k.t, t))));
    const indexIn = (a: FitAxis, b: (typeof shared)[number]): number => a.keys.findIndex((k) => Beats.eq(k.t, b));
    const keep = axes.map((a) => a.keys.map(() => true));
    const eases = axes.map((a) => a.keys.map((k) => k.ease));
    let i = 0;
    while (i < shared.length - 1) {
      if (!g.beats.has(Beats.key(shared[i] as (typeof shared)[number]))) {
        i++;
        continue;
      }
      let bestJ = -1;
      let bestFits: { ease: EaseValue; error: number }[] = [];
      let misses = 0;
      for (let j = i + 2; j < shared.length; j++) {
        // The run continues only through selected segments.
        if (!g.beats.has(Beats.key(shared[j - 1] as (typeof shared)[number]))) break;
        const fits = axes.map((a) =>
          bestEase(
            tx,
            a,
            indexIn(a, shared[i] as (typeof shared)[number]),
            indexIn(a, shared[j] as (typeof shared)[number]),
          ),
        );
        // A jump inside ends the run; a misfit does not (a longer span may fit again, e.g. a whole in-out curve
        // after its first quarter did not), but the search gives up after FIT_LOOKAHEAD misfits in a row.
        if (fits.some((f) => f === null)) break;
        if (fits.some((f) => f !== null && f.error > p.tolerance)) {
          if (++misses > FIT_LOOKAHEAD) break;
          continue;
        }
        misses = 0;
        bestJ = j;
        bestFits = fits as { ease: EaseValue; error: number }[];
      }
      if (bestJ < 0) {
        i++;
        continue;
      }
      axes.forEach((a, ai) => {
        const from = indexIn(a, shared[i] as (typeof shared)[number]);
        const to = indexIn(a, shared[bestJ] as (typeof shared)[number]);
        for (let k = from; k < to; k++) {
          const key = a.keys[k] as KeyRec;
          if (key.link !== 0) unlinked.add(key.link);
          if (k > from) (keep[ai] as boolean[])[k] = false;
        }
        const fitted = bestFits[ai];
        if (fitted !== undefined) {
          (eases[ai] as EaseValue[])[from] = fitted.ease;
          maxError = Math.max(maxError, fitted.error);
        }
      });
      i = bestJ;
    }
    axes.forEach((a, ai) => {
      const out: KeyRec[] = [];
      a.keys.forEach((k, idx) => {
        if (!(keep[ai] as boolean[])[idx]) return;
        const ease = (eases[ai] as EaseValue[])[idx] as EaseValue;
        if (ease !== k.ease) {
          k.ease = ease;
          k.link = 0;
        }
        out.push(k);
      });
      before += a.keys.length;
      after += out.length;
      removed += a.keys.length - out.length;
      if (out.length !== a.keys.length) commitKeys(tx, a.info, out);
      for (const k of out.slice(0, -1)) {
        if (k.id !== null && g.beats.has(Beats.key(k.t)) && keyAtBeat(tx, a.info.track.id, k.t) !== undefined) {
          segments.push(k.id);
        }
      }
    });
  }
  if (removed === 0) tx.fail(toolReason(TOOLS_MSG.fitNone, { tolerance: p.tolerance }));
  cleanLinks(tx, unlinked);
  const notes: MessageRef[] = [
    toolReason(TOOLS_MSG.fitDone, { before, after, error: Number(maxError.toPrecision(3)) }),
  ];
  for (const n of notes) tx.note(n);
  tx.setSelection({ segments, keys: [] });
  return { changed: removed, maxError, notes };
}
